/**
 * Requirements — the analyst's daily screen (IA spec §6.1).
 *
 * Rules pinned by tests:
 *  - a determination state NEVER renders as a bare enum: the plain-language
 *    gloss travels with it everywhere, and INSUFFICIENT renders as
 *    "Not concluded — input required" linked to its specific input;
 *  - the newer-inputs marker renders as a next-run statement, never as an
 *    updated determination;
 *  - load failure is a stated failure.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import { fmtTs } from '../utils/complianceFormat';

export const STATE_STYLES = {
  COMPLIANT: 'bg-green-900/40 text-green-300 border-green-700',
  PARTIAL: 'bg-blue-900/40 text-blue-300 border-blue-700',
  NONCOMPLIANT: 'bg-red-900/40 text-red-300 border-red-700',
  INSUFFICIENT_EVIDENCE: 'bg-amber-900/40 text-amber-300 border-amber-700',
  NEEDS_REVIEW: 'bg-purple-900/40 text-purple-300 border-purple-700',
  NOT_APPLICABLE: 'bg-gray-700/40 text-gray-400 border-gray-600',
  ACCEPTED_EXCEPTION: 'bg-gray-700/40 text-gray-300 border-gray-600',
};

export function GlossChip({ determination, gloss }) {
  // The gloss IS the rendering; the enum rides in a tooltip-style title,
  // never as the visible text on its own.
  return (
    <span title={determination}
          className={`inline-block px-2 py-0.5 text-[11px] rounded-full border whitespace-nowrap ${STATE_STYLES[determination] || STATE_STYLES.NOT_APPLICABLE}`}>
      {gloss}
    </span>
  );
}

export default function ComplianceRequirements() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/skills/compliance/requirements');
      if (!mounted.current) return;
      setDoc(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Requirements could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = (doc?.requirements || []).filter(
    (r) => filter === 'all' || r.determination === filter);
  const states = [...new Set((doc?.requirements || []).map((r) => r.determination))];

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-1">Requirements</h1>
      <p className="text-sm text-gray-400 mb-1">
        Where each assessed requirement part stands right now.
      </p>
      <p data-testid="requirements-framing" className="text-xs text-gray-500 mb-4">
        This lists only the parts assessed for this deployment — the full standard
        inventory, including what is deliberately not assessed and why, is on{' '}
        <Link to="/compliance/coverage" className="underline">Coverage</Link>.
      </p>

      {loading && <div className="text-gray-400 text-sm">Loading requirements…</div>}
      {error && (
        <div data-testid="requirements-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          {error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}

      {!loading && !error && doc && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            <button onClick={() => setFilter('all')}
                    className={`px-2 py-1 text-xs rounded-lg border ${filter === 'all' ? 'border-blue-500 text-white' : 'border-gray-600 text-gray-400'}`}>
              All ({doc.requirements.length})
            </button>
            {states.map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                      className={`px-2 py-1 text-xs rounded-lg border ${filter === s ? 'border-blue-500 text-white' : 'border-gray-600 text-gray-400'}`}>
                {(doc.requirements.find((r) => r.determination === s) || {}).determination_gloss}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.requirement_id} data-testid={`req-${r.requirement_id}`}
                   className="bg-gray-800 rounded-xl border border-gray-700 p-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <Link to={`/compliance/requirements/${r.requirement_id}`}
                        className="text-sm font-semibold text-white hover:underline font-mono">
                    {r.requirement_id}
                  </Link>
                  <div className="text-xs text-gray-400 mt-1">{r.title}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Assessed as of {fmtTs(r.assessed_as_of)}
                    {r.has_finding && <span className="text-red-300 ml-2">· finding attached</span>}
                  </div>
                  {r.determination === 'INSUFFICIENT_EVIDENCE' && (
                    <div className="text-xs mt-2" data-testid={`input-link-${r.requirement_id}`}>
                      {r.required_input_key ? (
                        <Link to={`/compliance/inputs`} className="text-amber-300 underline">
                          Not concluded — input required: supply it on the Inputs screen
                        </Link>
                      ) : (
                        <Link to={`/compliance/inputs`} className="text-amber-300 underline">
                          Not concluded — input required: see the Inputs screen for the second system needed
                        </Link>
                      )}
                    </div>
                  )}
                  {r.newer_inputs_exist && (
                    <div className="text-xs text-blue-300 mt-2" data-testid={`newer-${r.requirement_id}`}>
                      Newer inputs exist — this determination updates on the next assessment run.
                    </div>
                  )}
                </div>
                <GlossChip determination={r.determination} gloss={r.determination_gloss} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
    </div>
  );
}
